import { AttributesSelector } from '@workwhile/ui'
import React, { useMemo } from 'react'
import { FormProvider, useFormContext } from 'react-hook-form'
import { FiltersSchema } from '../filtersSchema'
import { ShiftIdsFilter } from './ShiftIdsFilter'
import { ListingIdsFilter } from './ListingIdsFilter'
import { LocationIdsFilter } from './LocationIdsFilter'
import { PositionIdsFilter } from './PositionIdsFilter'
import { MarketCodesFilter } from './MarketCodesFilter'
import { ScheduleRateFilter } from './ScheduleRateFilter'
import { FillRateFilter } from './FillRateFilter'
import { StatusesFilter } from './StatusesFilter/StatusesFilter'
import { shiftStatuses } from './StatusesFilter/constants'
import { rosterRestrictions } from './RosterRestrictions/constants'
import { RosterRestrictionsFilter } from './RosterRestrictions/RosterRestrictionsFilter'
import { IsStandbyFilter } from './IsStandbyFilter/IsStandbyFilter'

export const ShiftFilters = () => {
  const methods = useFormContext<FiltersSchema>()
  const shift = methods.watch('shift')

  const resetToDefaultValues = () => {
    methods.reset({
      ...methods.getValues(),
      shift: {
        ...methods.getValues('shift'),
        ids: [],
        positionIds: [],
        listingIds: [],
        locationIds: [],
        marketCodes: [],
        scheduleRateMin: undefined,
        scheduleRateMax: undefined,
        fillRateMin: undefined,
        fillRateMax: undefined,
        statuses: [],
        rosterRestrictions: [],
        isStandby: null,
      },
    })
  }

  const resetShiftIds = () => {
    methods.reset({
      ...methods.getValues(),
      shift: {
        ...methods.getValues('shift'),
        ids: [],
        positionIds: [],
      },
    })
  }

  const resetListingIds = () => {
    methods.reset({
      ...methods.getValues(),
      shift: {
        ...methods.getValues('shift'),
        listingIds: [],
      },
    })
  }

  const resetLocationIds = () => {
    methods.reset({
      ...methods.getValues(),
      shift: {
        ...methods.getValues('shift'),
        locationIds: [],
      },
    })
  }

  const resetPositionIds = () => {
    methods.reset({
      ...methods.getValues(),
      shift: {
        ...methods.getValues('shift'),
        positionIds: [],
      },
    })
  }

  const resetMarketCodes = () => {
    methods.reset({
      ...methods.getValues(),
      shift: {
        ...methods.getValues('shift'),
        marketCodes: [],
      },
    })
  }

  const resetScheduleRate = () => {
    methods.reset({
      ...methods.getValues(),
      shift: {
        ...methods.getValues('shift'),
        scheduleRateMin: null,
        scheduleRateMax: null,
      },
    })
  }

  const resetFillRate = () => {
    methods.reset({
      ...methods.getValues(),
      shift: {
        ...methods.getValues('shift'),
        fillRateMin: null,
        fillRateMax: null,
      },
    })
  }

  const resetStatuses = () => {
    methods.reset({
      ...methods.getValues(),
      shift: {
        ...methods.getValues('shift'),
        statuses: [],
      },
    })
  }

  const resetRosterTypes = () => {
    methods.reset({
      ...methods.getValues(),
      shift: {
        ...methods.getValues('shift'),
        rosterRestrictions: [],
      },
    })
  }

  const resetIsStandby = () => {
    methods.reset({
      ...methods.getValues(),
      shift: {
        ...methods.getValues('shift'),
        isStandby: null,
      },
    })
  }

  const scheduleRateSelectedAttributes = useMemo(() => {
    if (shift.scheduleRateMin && shift.scheduleRateMax) {
      return [
        `Schedule Rate ${shift.scheduleRateMin}% - ${shift.scheduleRateMax}%`,
      ]
    }

    if (shift.scheduleRateMin) {
      return [`Schedule Rate ${shift.scheduleRateMin}%`]
    }

    if (shift.scheduleRateMax) {
      return [`Schedule Rate ${shift.scheduleRateMax}%`]
    }

    return []
  }, [shift.scheduleRateMin, shift.scheduleRateMax])

  const fillRateSelectedAttributes = useMemo(() => {
    if (shift.fillRateMin && shift.fillRateMax) {
      return [`Fill Rate ${shift.fillRateMin}% - ${shift.fillRateMax}%`]
    }

    if (shift.fillRateMin) {
      return [`Fill Rate ${shift.fillRateMin}%`]
    }

    if (shift.fillRateMax) {
      return [`Fill Rate ${shift.fillRateMax}%`]
    }

    return []
  }, [shift.fillRateMin, shift.fillRateMax])

  const numberOfShiftIdsSelected = shift.ids?.length ?? 0

  const numberOfListingIdsSelected = shift.listingIds?.length ?? 0
  const numberOfLocationIdsSelected = shift.locationIds?.length ?? 0
  const numberOfPositionIdsSelected = shift.positionIds?.length ?? 0

  const numberOfMarketCodesSelected = shift.marketCodes?.length ?? 0

  const numberOfScheduleRateSelected = scheduleRateSelectedAttributes.length

  const numberOfFillRateSelected = fillRateSelectedAttributes.length

  const numberOfStatusesSelected = shift.statuses?.length ?? 0

  const numberOfRosterTypesSelected = shift.rosterRestrictions?.length ?? 0

  const isStandbySelectedAttributes = useMemo(() => {
    if (shift.isStandby === true) return ['Standby']
    if (shift.isStandby === false) return ['Non-standby']
    return []
  }, [shift.isStandby])

  const selectedStatuses = useMemo(() => {
    if (numberOfStatusesSelected === 0) {
      return []
    }

    if (numberOfStatusesSelected === 1) {
      const foundStatus = shiftStatuses.find(
        (status) => status.id === shift.statuses?.[0]
      )
      if (foundStatus) {
        return [foundStatus.label]
      }
    }

    if (numberOfStatusesSelected > 1) {
      const pluralRules = new Intl.PluralRules('en-US')
      const plural = pluralRules.select(numberOfStatusesSelected)
      const suffix = plural === 'one' ? 'Status' : 'Statuses'
      return [`${numberOfStatusesSelected} ${suffix}`]
    }

    return []
  }, [numberOfStatusesSelected, shift.statuses])

  const selectedRosterTypes = useMemo(() => {
    if (numberOfRosterTypesSelected === 0) {
      return []
    }

    if (numberOfRosterTypesSelected === 1) {
      const foundType = rosterRestrictions.find(
        (type) => type.id === shift.rosterRestrictions?.[0]
      )
      if (foundType) {
        return [`${foundType.label} roster`]
      }
    }

    if (numberOfRosterTypesSelected > 1) {
      const pluralRules = new Intl.PluralRules('en-US')
      const plural = pluralRules.select(numberOfRosterTypesSelected)
      const suffix = plural === 'one' ? 'Roster Type' : 'Roster Types'
      return [`${numberOfRosterTypesSelected} ${suffix}`]
    }

    return []
  }, [numberOfRosterTypesSelected, shift.rosterRestrictions])

  const selectedShiftAttributes = useMemo(() => {
    const fields: string[] = []
    const pluralRules = new Intl.PluralRules('en-US')

    if (numberOfShiftIdsSelected > 0) {
      const plural = pluralRules.select(numberOfShiftIdsSelected)
      const suffix = plural === 'one' ? 'Shift' : 'Shifts'
      fields.push(`${numberOfShiftIdsSelected} ${suffix}`)
    }

    if (numberOfPositionIdsSelected > 0) {
      const plural = pluralRules.select(numberOfPositionIdsSelected)
      const suffix = plural === 'one' ? 'Position' : 'Positions'
      fields.push(`${numberOfPositionIdsSelected} ${suffix}`)
    }

    if (numberOfListingIdsSelected > 0) {
      const plural = pluralRules.select(numberOfListingIdsSelected)
      const suffix = plural === 'one' ? 'Listing' : 'Listings'
      fields.push(`${numberOfListingIdsSelected} ${suffix}`)
    }

    if (numberOfLocationIdsSelected > 0) {
      const plural = pluralRules.select(numberOfLocationIdsSelected)
      const suffix = plural === 'one' ? 'Location' : 'Locations'
      fields.push(`${numberOfLocationIdsSelected} ${suffix}`)
    }

    if (scheduleRateSelectedAttributes.length > 0) {
      fields.push(...scheduleRateSelectedAttributes)
    }

    if (fillRateSelectedAttributes.length > 0) {
      fields.push(...fillRateSelectedAttributes)
    }

    if (numberOfMarketCodesSelected > 0) {
      const plural = pluralRules.select(numberOfMarketCodesSelected)
      const suffix = plural === 'one' ? 'Market' : 'Markets'
      fields.push(`${numberOfMarketCodesSelected} ${suffix}`)
    }

    if (numberOfStatusesSelected > 0) {
      fields.push(...selectedStatuses)
    }

    if (numberOfRosterTypesSelected > 0) {
      fields.push(...selectedRosterTypes)
    }

    if (isStandbySelectedAttributes.length > 0) {
      fields.push(...isStandbySelectedAttributes)
    }

    return fields
  }, [
    numberOfShiftIdsSelected,
    numberOfPositionIdsSelected,
    numberOfListingIdsSelected,
    numberOfLocationIdsSelected,
    numberOfMarketCodesSelected,
    numberOfStatusesSelected,
    numberOfRosterTypesSelected,
    scheduleRateSelectedAttributes,
    fillRateSelectedAttributes,
    selectedStatuses,
    selectedRosterTypes,
    isStandbySelectedAttributes,
  ])

  return (
    <FormProvider {...methods}>
      <AttributesSelector
        label="Shift"
        rootGroupTitle="Filter by"
        selectedAttributes={selectedShiftAttributes}
        onClearAll={resetToDefaultValues}
        selectedAttributeLimit={5}
        groups={[
          <AttributesSelector.Group
            key="shift-ids"
            title="Shift IDs"
            numberOfSelectedAttributes={numberOfShiftIdsSelected}
            onClearAll={resetShiftIds}
          >
            <ShiftIdsFilter />
          </AttributesSelector.Group>,
          <AttributesSelector.Group
            key="listing-ids"
            title="Listing IDs"
            numberOfSelectedAttributes={numberOfListingIdsSelected}
            onClearAll={resetListingIds}
          >
            <ListingIdsFilter />
          </AttributesSelector.Group>,
          <AttributesSelector.Group
            key="location-ids"
            title="Location IDs"
            numberOfSelectedAttributes={numberOfLocationIdsSelected}
            onClearAll={resetLocationIds}
          >
            <LocationIdsFilter />
          </AttributesSelector.Group>,
          <AttributesSelector.Group
            key="statuses"
            title="Statuses"
            numberOfSelectedAttributes={numberOfStatusesSelected}
            onClearAll={resetStatuses}
          >
            <StatusesFilter />
          </AttributesSelector.Group>,
          <AttributesSelector.Group
            key="schedule-rate"
            title="Schedule Rate"
            numberOfSelectedAttributes={numberOfScheduleRateSelected}
            onClearAll={resetScheduleRate}
          >
            <ScheduleRateFilter />
          </AttributesSelector.Group>,
          <AttributesSelector.Group
            key="fill-rate"
            title="Fill Rate"
            numberOfSelectedAttributes={numberOfFillRateSelected}
            onClearAll={resetFillRate}
          >
            <FillRateFilter />
          </AttributesSelector.Group>,
          <AttributesSelector.Group
            key="roster-restrictions"
            title="Roster Restrictions"
            numberOfSelectedAttributes={numberOfRosterTypesSelected}
            onClearAll={resetRosterTypes}
          >
            <RosterRestrictionsFilter />
          </AttributesSelector.Group>,
          <AttributesSelector.Group
            key="market-codes"
            title="Market Codes"
            numberOfSelectedAttributes={numberOfMarketCodesSelected}
            onClearAll={resetMarketCodes}
          >
            <MarketCodesFilter />
          </AttributesSelector.Group>,
          <AttributesSelector.Group
            key="position-ids"
            title="Position IDs"
            numberOfSelectedAttributes={numberOfPositionIdsSelected}
            onClearAll={resetPositionIds}
          >
            <PositionIdsFilter />
          </AttributesSelector.Group>,
          <AttributesSelector.Group
            key="is-standby"
            title="Standby"
            numberOfSelectedAttributes={isStandbySelectedAttributes.length}
            onClearAll={resetIsStandby}
          >
            <IsStandbyFilter />
          </AttributesSelector.Group>,
        ]}
      />
    </FormProvider>
  )
}
